import React, { FC, useCallback, useMemo } from 'react';

import { Control, useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { MIN_USD_PRICE } from 'constants/constants';
import { OfferDesignSelector } from 'views/Offers/components/OfferDesignSelector';
import { LOCALIZED_PRICE_ERROR } from 'views/Offers/Offer.consts';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import Textarea from 'components/Textarea/Textarea';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from 'components/ui';

import { IDropdownOption, IGeneralSectionProps } from './sections.types';

interface ITextFieldConfig {
  name: string;
  label: string;
  placeholder: string;
  isRequired?: boolean;
  component: 'input' | 'textarea';
  descriptionDataTestId?: string;
}

const TEXT_FIELDS: ITextFieldConfig[] = [
  { name: 'name', label: 'Name', placeholder: 'Name', isRequired: true, component: 'input' },
  { name: 'displayName', label: 'Display Name', placeholder: 'Display Name', component: 'input' },
  { name: 'description', label: 'Description', placeholder: 'Placeholder', component: 'textarea' },
  { name: 'publisherOfferId', label: 'External ID', placeholder: 'External ID', isRequired: true, component: 'input' }
];

interface ITextFormFieldProps {
  control: Control;
  config: ITextFieldConfig;
  customMessage?: React.ReactNode;
}

const TextFormField: FC<ITextFormFieldProps> = ({
  control,
  config,
  customMessage
}): React.ReactElement => {
  const renderField = useCallback(
    ({ field }: { field: any }): React.ReactElement => {
      let fieldElement: React.ReactElement;

      if (config.component === 'textarea') {
        fieldElement = (
          <Textarea
            label={config.label}
            placeholder={config.placeholder}
            {...field}
          />
        );
      } else {
        fieldElement = (
          <Input
            isRequired={config.isRequired}
            label={config.label}
            placeholder={config.placeholder}
            {...field}
          />
        );
      }

      const messageNode = customMessage ?? <FormMessage />;
      const messageWithTestId =
        config.descriptionDataTestId && messageNode
          ? <span data-testid={config.descriptionDataTestId}>{messageNode}</span>
          : messageNode;

      return (
        <FormItem>
          <FormControl>{fieldElement}</FormControl>
          {messageWithTestId}
        </FormItem>
      );
    },
    [config, customMessage]
  );

  return (
    <FormField
      control={control}
      name={config.name}
      render={renderField}
    />
  );
};

interface IDropdownFormFieldProps {
  control: Control;
  name: string;
  label: string;
  placeholder: string;
  options: IDropdownOption[];
  onChange: (value: string) => void;
  listDataTestId?: string;
  buttonDataTestId?: string;
  dataTestId?: string;
}

const DropdownFormField: FC<IDropdownFormFieldProps> = ({
  control,
  name,
  label,
  placeholder,
  options,
  onChange,
  listDataTestId,
  buttonDataTestId,
  dataTestId
}): React.ReactElement => {
  const renderField = useCallback(
    ({ field: { value } }: { field: { value: string } }): React.ReactElement => {
      const selected = options.find((item) => item.value === value);
      return (
        <FormItem>
          <FormControl>
            <InputSelectDropdown
              label={label}
              placeholder={placeholder}
              options={options}
              defaultSelected={selected}
              onOptionChange={onChange}
              listDataTestId={listDataTestId}
              buttonDataTestId={buttonDataTestId}
              dataTestId={dataTestId}
            />
          </FormControl>
        </FormItem>
      );
    },
    [options, label, placeholder, onChange, listDataTestId, buttonDataTestId, dataTestId]
  );

  return (
    <FormField
      control={control}
      name={name}
      render={renderField}
    />
  );
};

export const GeneralSection: FC<IGeneralSectionProps> = ({
  offerDesignOptions,
  onOfferDesignChange,
  isNameTaken,
  isPublisherOfferIdTaken,
  isPriceLocalized = true,
  sectionItems,
  tabItems,
  showSection = false,
  showTabs = false,
  hidePrice = false,
  priceConfig,
  children,
  offerDesignListDataTestId = 'offerDesignList',
  offerDesignButtonDataTestId,
  offerDesignDataTestId,
  offerDesignDescriptionDataTestId,
  sectionDataTestId,
  tabDataTestId,
  nameDescriptionDataTestId,
  externalIdDescriptionDataTestId,
  priceDescriptionDataTestId
}): React.ReactElement => {
  const { control, setValue } = useFormContext();

  const handleOfferDesignChange = useCallback(
    (value: string): void => {
      setValue('offerUiId', value, { shouldDirty: true, shouldValidate: true });
      onOfferDesignChange?.(value);
    },
    [setValue, onOfferDesignChange]
  );

  const handleSectionChange = useCallback(
    (value: string): void => {
      setValue('section', value, { shouldDirty: true });
    },
    [setValue]
  );

  const handleTabChange = useCallback(
    (value: string): void => {
      setValue('tab', value, { shouldDirty: true });
    },
    [setValue]
  );

  const isPriceDisabled = priceConfig?.disabled === true;
  const priceType = priceConfig?.type ?? 'number';
  const priceMin = priceConfig?.min ?? MIN_USD_PRICE;
  const priceLeftIcon = useMemo((): React.ReactElement => {
    if (priceConfig?.leftIcon) {
      return priceConfig.leftIcon;
    }
    return <FontAwesomeIcon icon="fa-regular fa-dollar-sign" />;
  }, [priceConfig?.leftIcon]);
  const priceDisplayValue = priceConfig?.displayValue ?? 'Free';

  const nameField = TEXT_FIELDS[0];
  const displayNameField = TEXT_FIELDS[1];
  const descriptionField = TEXT_FIELDS[2];
  const externalIdField = TEXT_FIELDS[3];

  const nameMessage = useMemo((): React.ReactNode => {
    if (isNameTaken) {
      return <FormMessage>Name already exists</FormMessage>;
    }
    return <FormMessage />;
  }, [isNameTaken]);

  const externalIdMessage = useMemo((): React.ReactNode => {
    if (isPublisherOfferIdTaken) {
      return <FormMessage>External ID already exists</FormMessage>;
    }
    return <FormMessage />;
  }, [isPublisherOfferIdTaken]);

  const priceMessage = useMemo((): React.ReactNode => {
    if (!isPriceLocalized && !isPriceDisabled) {
      return <FormMessage>{LOCALIZED_PRICE_ERROR}</FormMessage>;
    }
    return <FormMessage />;
  }, [isPriceLocalized, isPriceDisabled]);

  const handlePriceChange = useCallback(
    (onChange: (val: number | null) => void) =>
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (isPriceDisabled) return;
        const parsed = e.target.value === '' ? null : parseFloat(e.target.value);
        onChange(parsed);
      },
    [isPriceDisabled]
  );

  const handlePriceBlur = useCallback(
    (value: number | null, onChange: (val: number) => void, onBlur: () => void) =>
      (): void => {
        if (isPriceDisabled) return;
        if (value == null || value < priceMin) {
          onChange(priceMin);
        }
        onBlur();
      },
    [isPriceDisabled, priceMin]
  );

  const renderOfferDesignField = useCallback(
    ({ field: { value } }: { field: { value: string } }): React.ReactElement => {
      const messageNode = <FormMessage />;
      const messageWithTestId = offerDesignDescriptionDataTestId
        ? <span data-testid={offerDesignDescriptionDataTestId}>{messageNode}</span>
        : messageNode;
      return (
        <FormItem>
          <FormControl>
            <OfferDesignSelector
              options={offerDesignOptions}
              selectedValue={value}
              onChange={handleOfferDesignChange}
              listDataTestId={offerDesignListDataTestId}
              buttonDataTestId={offerDesignButtonDataTestId}
              dataTestId={offerDesignDataTestId}
              placeholder="Select Offer Design"
            />
          </FormControl>
          {messageWithTestId}
        </FormItem>
      );
    },
    [
      offerDesignOptions,
      handleOfferDesignChange,
      offerDesignListDataTestId,
      offerDesignButtonDataTestId,
      offerDesignDataTestId,
      offerDesignDescriptionDataTestId
    ]
  );

  const renderPriceField = useCallback(
    ({
      field: { value, onChange, onBlur }
    }: {
      field: { value: number | null; onChange: (val: number | null) => void; onBlur: () => void };
    }): React.ReactElement => {
      let displayedValue: string | number = value ?? '';
      if (isPriceDisabled) {
        displayedValue = priceDisplayValue;
      }

      let priceLeftIconValue: React.ReactElement | undefined = priceLeftIcon;
      if (isPriceDisabled) {
        priceLeftIconValue = undefined;
      }

      const priceMessageNode = priceMessage;
      const priceMessageWithTestId = priceDescriptionDataTestId
        ? <span data-testid={priceDescriptionDataTestId}>{priceMessageNode}</span>
        : priceMessageNode;

      return (
        <FormItem>
          <FormControl>
            <Input
              name="price"
              label="Price"
              isRequired={!isPriceDisabled}
              leftIcon={priceLeftIconValue}
              type={priceType}
              min={priceMin}
              step={1}
              value={displayedValue}
              disabled={isPriceDisabled}
              onChange={handlePriceChange(onChange)}
              onBlur={handlePriceBlur(value, onChange as (val: number) => void, onBlur)}
            />
          </FormControl>
          {priceMessageWithTestId}
        </FormItem>
      );
    },
    [
      isPriceDisabled,
      priceDisplayValue,
      priceLeftIcon,
      priceType,
      priceMin,
      handlePriceChange,
      handlePriceBlur,
      priceMessage,
      priceDescriptionDataTestId
    ]
  );

  const showTabsRow = showTabs && tabItems && tabItems.length > 0;

  return (
    <React.Fragment>
      <RichText
        title="General"
        description="Start with setting up your offer mandatory fields"
      />
      <div className="flex flex-col gap-5 mb-2">
        <div className="grid grid-cols-2 gap-5">
          <TextFormField
            control={control}
            config={{ ...nameField, descriptionDataTestId: nameDescriptionDataTestId }}
            customMessage={nameMessage}
          />
          <TextFormField control={control} config={displayNameField} />
        </div>
        <TextFormField control={control} config={descriptionField} />
        <div className="grid grid-cols-2 gap-5">
          <TextFormField
            control={control}
            config={{ ...externalIdField, descriptionDataTestId: externalIdDescriptionDataTestId }}
            customMessage={externalIdMessage}
          />
          <FormField
            control={control}
            name="offerUiId"
            render={renderOfferDesignField}
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          {showSection && sectionItems && (
            <DropdownFormField
              control={control}
              name="section"
              label="Section"
              placeholder="Select Section"
              options={sectionItems}
              onChange={handleSectionChange}
              dataTestId={sectionDataTestId}
            />
          )}
          {!hidePrice && (
            <FormField
              control={control}
              name="price"
              render={renderPriceField}
            />
          )}
        </div>
        {showTabsRow && (
          <div className="grid grid-cols-2 gap-5">
            <DropdownFormField
              control={control}
              name="tab"
              label="Tab"
              placeholder="Select Tab"
              options={tabItems!}
              onChange={handleTabChange}
              dataTestId={tabDataTestId}
            />
          </div>
        )}
        {children}
      </div>
    </React.Fragment>
  );
};
